import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import type { EmailAccount, EmailAccountInput, EmailAccountTestResult } from './types';

const ACCOUNT = ['email', 'account'] as const;
const FOLDERS = ['email', 'folders'] as const;
const DUMPS = ['braindumps'] as const;

export function useEmailAccount() {
  return useQuery<EmailAccount>({
    queryKey: ACCOUNT,
    queryFn: () => api<EmailAccount>('/api/email/account'),
  });
}

export function useEmailFolders(enabled: boolean) {
  return useQuery<{ folders: string[] }>({
    queryKey: FOLDERS,
    queryFn: () => api<{ folders: string[] }>('/api/email/account/folders'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveEmailAccount() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, ApiError, EmailAccountInput>({
    mutationFn: (body) => api<EmailAccount>('/api/email/account', { method: 'PUT', json: body }),
    onSuccess: (data) => {
      qc.setQueryData(ACCOUNT, data);
      qc.invalidateQueries({ queryKey: ACCOUNT });
      qc.invalidateQueries({ queryKey: FOLDERS });
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteEmailAccount() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => api<void>('/api/email/account', { method: 'DELETE' }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: FOLDERS });
      qc.invalidateQueries({ queryKey: ACCOUNT });
    },
    onError: (error) => toast.error(error.message),
  });
}

function folderToast(data: EmailAccountTestResult): string {
  if (data.total_count === 0) {
    return `Connected. ${data.folder} is empty`;
  }
  if (data.unseen_count === 0) {
    return (
      `Connected. ${data.total_count} message${data.total_count === 1 ? '' : 's'} in ${data.folder}, ` +
      'but none unread on the server (webmail may track read status separately)'
    );
  }
  return `${data.unseen_count} unread in ${data.folder} (${data.total_count} total)`;
}

export function useTestEmailAccount() {
  const qc = useQueryClient();
  return useMutation<EmailAccountTestResult, ApiError, { folder?: string } | void>({
    mutationFn: (body) =>
      api<EmailAccountTestResult>('/api/email/account/test', {
        method: 'POST',
        json: body ?? {},
      }),
    onSuccess: (data) => {
      toast.show(folderToast(data));
      qc.setQueryData(FOLDERS, { folders: data.folders });
      qc.invalidateQueries({ queryKey: ACCOUNT });
    },
    onError: (error) => toast.error(error.message),
  });
}

export function usePollEmail() {
  const qc = useQueryClient();
  return useMutation<{ queued: boolean }, ApiError, void>({
    mutationFn: () => api<{ queued: boolean }>('/api/email/poll', { method: 'POST' }),
    onSuccess: () => {
      toast.show('Checking for mail: summary updates when the check finishes');
      qc.invalidateQueries({ queryKey: ACCOUNT });
      qc.invalidateQueries({ queryKey: DUMPS });
      // Poll again after the worker has had time to finish.
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ACCOUNT });
        qc.invalidateQueries({ queryKey: DUMPS });
      }, 8000);
    },
    onError: (error) => toast.error(error.message),
  });
}
